export default class MobileMenu {
    private mobileMenuContainer!: HTMLElement;
    private mobileMenuTrigger!: HTMLElement;
    private activeMenuId!: string;
    private mainMenuId = "menu-0";
    private menuSelector = 'ul[id^="menu-"]';

    constructor() {
        const container = document.getElementById("mobile-menu-container");
        const trigger = document.getElementById("mobile-menu-trigger");
        if (!container || !trigger) return;

        this.mobileMenuContainer = container;
        this.mobileMenuTrigger = trigger;
        this.activeMenuId = this.mainMenuId;

        this.setPrimaryMenu();
        this.setEventListeners();
        this.setFocusTrap();
    }

    private setEventListeners() {
        this.mobileMenuTrigger.addEventListener("click", () => {
            this.setPrimaryMenu();
            // The Trigger component toggles `mobile-menu-open` on the same click;
            // its listener runs after this one, so the body class still reflects
            // the *pre-toggle* state here.
            const willBeOpen = !document.body.classList.contains("mobile-menu-open");
            const label = this.mobileMenuTrigger.querySelector("span");
            if (willBeOpen) {
                if (label) label.innerText = "Sluit";
                window.requestAnimationFrame(() => this.focusFirstItem());
            } else {
                if (label) label.innerText = "Menu";
                this.mobileMenuTrigger.focus();
            }
            this.mobileMenuTrigger.classList.toggle("icon-menu");
            this.mobileMenuTrigger.classList.toggle("icon-cancel");
        });

        Array.from(this.mobileMenuContainer.querySelectorAll("li.has-children > button")).map((submenuButton) => {
            submenuButton.addEventListener("click", () => {
                const submenuId = submenuButton.getAttribute("aria-controls");
                if (!submenuId) return;
                this.setMenu(submenuId);
                window.requestAnimationFrame(() => this.focusFirstInActiveMenu());
            });
        });

        Array.from(this.mobileMenuContainer.querySelectorAll(".sub-level > li > button.back-to-menu-button")).map(
            (backButton) => {
                backButton.addEventListener("click", () => {
                    this.setPrimaryMenu();
                    window.requestAnimationFrame(() => this.focusFirstInActiveMenu());
                });
            }
        );

        window.addEventListener("keydown", (e) => {
            if (e.key === "Escape" && document.body.classList.contains("mobile-menu-open")) {
                this.mobileMenuTrigger.focus();
            }
        });
    }

    private setPrimaryMenu() {
        this.setMenu(this.mainMenuId);
    }

    private focusFirstItem() {
        const first = this.getVisibleFocusable()[0];
        if (first instanceof HTMLElement) {
            // preventScroll: the off-screen sub-menus (translateX(100%)) push the
            // container's scrollWidth past its clientWidth; without this flag a
            // browser may auto-scroll the container during the focus call and
            // leave the active menu at scrollLeft=640 (off-screen left).
            first.focus({ preventScroll: true });
        }
    }

    private focusFirstInActiveMenu() {
        const active = this.mobileMenuContainer.querySelector("#" + this.activeMenuId);
        if (!active) return;
        const focusable = active.querySelector<HTMLElement>(
            'a[href]:not([disabled]), button:not([disabled]), input:not([type="hidden"]):not([disabled])'
        );
        if (focusable) {
            focusable.focus({ preventScroll: true });
        }
    }

    private getVisibleFocusable(): Element[] {
        const selector =
            'a[href]:not([disabled]), button:not([disabled]), textarea:not([disabled]), input:not([type="hidden"]):not([disabled]), select:not([disabled])';
        return Array.from(this.mobileMenuContainer.querySelectorAll(selector)).filter((el) => {
            // Skip anything the browser also won't tab to: inside an `inert`
            // subtree, or laid out off the page (offsetParent === null).
            if (el.closest("[inert]")) return false;
            return (el as HTMLElement).offsetParent !== null;
        });
    }

    /**
     * Keep keyboard focus inside the open mobile menu so the user can never tab
     * onto the underlying page while the menu is still on screen (ZOA-2.F42).
     * Recomputes focusable elements on each Tab press to follow submenu state.
     */
    private setFocusTrap() {
        this.mobileMenuContainer.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            if (!document.body.classList.contains("mobile-menu-open")) return;

            const focusable = this.getVisibleFocusable();
            if (focusable.length === 0) return;

            const first = focusable[0] as HTMLElement;
            const last = focusable[focusable.length - 1] as HTMLElement;
            const active = document.activeElement;

            if (e.shiftKey && active === first) {
                last.focus();
                e.preventDefault();
            } else if (!e.shiftKey && active === last) {
                first.focus();
                e.preventDefault();
            }
        });
    }

    /**
     * Activate a single menu (`menu-0` for the root, or a `menu-<uid>` sub-menu).
     * The previously-active menu is marked `aria-hidden=true` AND `inert` so its
     * children leave the focus order entirely; otherwise tab navigation walks
     * through items that are visually hidden behind the active layer.
     */
    private setMenu(menuId: string) {
        this.activeMenuId = menuId;

        Array.from(this.mobileMenuContainer.querySelectorAll(this.menuSelector)).forEach((menu) => {
            const isActive = menu.id === menuId;
            menu.setAttribute("aria-hidden", isActive ? "false" : "true");
            if (isActive) {
                menu.removeAttribute("inert");
            } else {
                menu.setAttribute("inert", "");
            }
        });

        Array.from(this.mobileMenuContainer.querySelectorAll(`${this.menuSelector} > li`)).forEach((menuItem) => {
            menuItem.setAttribute("aria-expanded", "false");
        });

        // The off-screen sub-menus extend the container's scrollWidth past its
        // clientWidth; if a previous focus()/scroll-into-view shifted it, snap
        // it back so the active menu lines up with the viewport.
        this.mobileMenuContainer.scrollLeft = 0;
    }
}
